import { CreateReportButton } from "@/components/CreateReportButton";
import { PageActions } from "@/components/PageActions";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, ClipboardList, Activity, AlertTriangle, TrendingUp, Rocket,
  Calendar, Plus, X, Loader2, Search, Filter, Users, Package, Eye,
  ChevronRight, MessageSquare, Sparkles, Target
} from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import SupportTicketsInbox from "@/components/support/SupportTicketsInbox";

// ─── constantes ─────────────────────────────────────────────────────────
const TABS = [
  { k: "cockpit", l: "Marketing / Com", icon: Megaphone },
  { k: "offre", l: "Offre & Campagnes", icon: Rocket },
  { k: "activite", l: "Activité (Demandes)", icon: Activity },
];
const PRIO = {
  urgent: { l: "Urgent", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  important: { l: "Important", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  standard: { l: "Standard", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const CAT_LABEL = { plv: "PLV / Affichage", communication_locale: "Com. locale", reseaux_sociaux: "Réseaux sociaux", support_commercial: "Support commercial", modification_support: "Modif. support", animation: "Animation", offre_promo: "Offre / promo", signaletique: "Signalétique", fichiers: "Fichiers", probleme_communication: "Problème com.", menu: "Menu", autre: "Autre" };
const REQ_STAT_LABEL = { nouvelle: "Nouvelle", a_qualifier: "À qualifier", affectee: "Affectée", en_production: "En production", a_valider: "À valider", corrections: "Corrections", prete: "Prête", diffusee: "Diffusée", cloturee: "Clôturée", attente_terrain: "Attente terrain", bloquee: "Bloquée" };
const REQ_STAT_CLS = { nouvelle: "bg-blue-100 text-blue-700", a_qualifier: "bg-cyan-100 text-cyan-700", affectee: "bg-indigo-100 text-indigo-700", en_production: "bg-purple-100 text-purple-700", a_valider: "bg-amber-100 text-amber-700", corrections: "bg-orange-100 text-orange-700", prete: "bg-teal-100 text-teal-700", diffusee: "bg-emerald-100 text-emerald-700", cloturee: "bg-slate-200 text-slate-600", attente_terrain: "bg-yellow-100 text-yellow-800", bloquee: "bg-rose-100 text-rose-700" };
const CAMP_STAT_CLS = { preparation: "bg-slate-100 text-slate-600", production: "bg-purple-100 text-purple-700", validation: "bg-amber-100 text-amber-700", kit_dispo: "bg-cyan-100 text-cyan-700", deploiement: "bg-blue-100 text-blue-700", active: "bg-emerald-100 text-emerald-700", terminee: "bg-slate-200 text-slate-600", annulee: "bg-rose-100 text-rose-700" };
const DONUT_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#94a3b8", "#06b6d4", "#eab308"];

export default function MarketingHub() {
  const [sp, setSp] = useSearchParams();
  const active = sp.get("s") || "cockpit";
  const [filters, setFilters] = useState({ dr: "", secteur: "", corner_key: "", annee: "", mois: "" });
  const [filterOpts, setFilterOpts] = useState({ drs: [], secteurs: [], corners: [], annees: [], mois: [] });

  useEffect(() => {
    api.get("/maintenance/filter-options").then(r => setFilterOpts({
      drs: r.data?.drs || [], secteurs: r.data?.secteurs || [], corners: r.data?.corners || [],
      annees: r.data?.annees || [], mois: r.data?.mois || [],
    })).catch(() => {});
  }, []);

  return (
    <div className="space-y-4" data-testid="mkt-hub-page">
      {/* Onglets */}
      <div className="bg-white border border-slate-200 rounded-2xl p-1.5 flex gap-1 overflow-x-auto sticky top-0 z-20 shadow-sm items-center">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setSp({ s: t.k })} data-testid={`mkt-hub-tab-${t.k}`}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition ${active === t.k ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}>
            <t.icon className="w-4 h-4"/> {t.l}
          </button>
        ))}
        <PageActions title="Marketing / Communication" filters={() => document.querySelector("[data-testid=mkt-filterbar]")?.innerText?.replace(/\s+/g, " ").slice(0, 160) || ""} testid="mkt-actions" />
      </div>

      <SupportTicketsInbox service="Marketing" testid="mkt-tickets-inbox"/>

      {/* Filtres communs */}
      <div className="bg-white rounded-2xl border border-slate-200 p-2.5 flex items-center gap-2 flex-wrap" data-testid="mkt-filterbar">
        <Filter className="w-4 h-4 text-slate-400 ml-1"/>
        <FSelect value={filters.dr} onChange={v => setFilters({...filters, dr: v})} options={filterOpts.drs} placeholder="DR" testid="mkt-f-dr"/>
        <FSelect value={filters.secteur} onChange={v => setFilters({...filters, secteur: v})} options={filterOpts.secteurs} placeholder="Secteur" testid="mkt-f-secteur"/>
        <FSelect value={filters.corner_key} onChange={v => setFilters({...filters, corner_key: v})} options={(filterOpts.corners || []).map(c => ({ v: c.corner_key, l: c.corner || c.corner_key }))} enrich placeholder="Corner" testid="mkt-f-corner"/>
        <FSelect value={filters.annee} onChange={v => setFilters({...filters, annee: v})} options={(filterOpts.annees || []).map(y => ({ v: String(y), l: String(y) }))} enrich placeholder="Année" testid="mkt-f-annee"/>
        <FSelect value={filters.mois} onChange={v => setFilters({...filters, mois: v})} options={(filterOpts.mois || []).map(m => ({ v: m.k, l: m.l }))} enrich placeholder="Mois" testid="mkt-f-mois"/>
        {Object.values(filters).some(Boolean) && (
          <button onClick={() => setFilters({ dr: "", secteur: "", corner_key: "", annee: "", mois: "" })} className="ml-auto text-xs font-bold text-slate-500 hover:text-slate-800 flex items-center gap-1 px-2" data-testid="mkt-f-reset">
            <X className="w-3 h-3"/> Reset
          </button>
        )}
      </div>

      {active === "cockpit" && <CockpitTab filters={filters}/>}
      {active === "offre" && <OffreTab filters={filters}/>}
      {active === "activite" && <ActiviteTab filters={filters}/>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  COCKPIT
// ═════════════════════════════════════════════════════════════════════════
function CockpitTab({ filters }) {
  const [dash, setDash] = useState(null); const [loading, setLoading] = useState(true); const [seeding, setSeeding] = useState(false);
  const load = async () => { setLoading(true); try { const r = await api.get("/marketing/cockpit", { params: filters }); setDash(r.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); } setLoading(false); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.dr, filters.secteur, filters.corner_key, filters.annee, filters.mois]);
  const seed = async () => { setSeeding(true); try { const r = await api.post("/marketing/seed"); toast.success(r.data.skipped ? r.data.message : "Seed OK"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); } setSeeding(false); };
  const isEmpty = dash && !dash.a_traiter.length && !dash.calendrier.length && !dash.campagnes_en_cours.length;

  return (
    <div className="space-y-3" data-testid="mkt-cockpit">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Cockpit Marketing / Communication</h1><CreateReportButton module="marketing" className="mt-2 ml-2"/>
          <p className="text-slate-500 mt-1 text-sm">Vue d'ensemble des demandes terrain, campagnes et déploiements réseau.</p>
        </div>
      </div>

      {isEmpty && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between gap-3">
          <div className="text-sm text-amber-800"><b>Aucune donnée Marketing.</b> Lance un seed démo pour visualiser le module.</div>
          <button onClick={seed} disabled={seeding} data-testid="mkt-seed" className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50">
            {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>} Générer données démo
          </button>
        </div>
      )}

      {loading && !dash ? (
        <div className="py-24 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div>
      ) : dash && (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi icon={ClipboardList} c="blue" title="Demandes à traiter" v={dash.kpis.demandes_traiter.value} s={`dont ${dash.kpis.demandes_traiter.urgentes} urgentes`} testid="mkt-kpi-demandes"/>
            <Kpi icon={Package} c="purple" title="Productions en cours" v={dash.kpis.productions_encours.value} s={`dont ${dash.kpis.productions_encours.retard} en retard`} testid="mkt-kpi-prod"/>
            <Kpi icon={AlertTriangle} c="rose" title="Urgences" v={dash.kpis.urgences.value} testid="mkt-kpi-urg"/>
            <Kpi icon={Rocket} c="emerald" title="Campagnes actives" v={dash.kpis.campagnes_actives.value} testid="mkt-kpi-camp"/>
            <Kpi icon={Calendar} c="amber" title="Déploiements 7j" v={dash.kpis.deploiements_7j.value} s="prochains lancements" testid="mkt-kpi-depl"/>
            <Kpi icon={TrendingUp} c="orange" title="Taux déploiement" v={dash.kpis.taux_deploiement.value} unit="%" s={`objectif ${dash.kpis.taux_deploiement.objectif}%`} testid="mkt-kpi-taux"/>
          </div>

          {/* Row 1 : À traiter + Calendrier + Conformité */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="mkt-a-traiter">
              <h3 className="font-display text-base font-bold text-slate-900 mb-2">À traiter en priorité</h3>
              <div className="divide-y divide-slate-100">
                {dash.a_traiter.map(r => {
                  const p = PRIO[r.priorite] || PRIO.standard;
                  return (
                    <div key={r.id} className="py-2 flex items-center gap-2" data-testid={`mkt-at-${r.id}`}>
                      <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full border ${p.cls}`}>{p.l}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-slate-800 truncate">{r.sujet}</div>
                        <div className="text-[10px] text-slate-500 truncate">{r.corner || "—"} · {CAT_LABEL[r.categorie] || r.categorie}</div>
                      </div>
                      <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap">Depuis {r.age_h < 24 ? `${r.age_h}h` : `${Math.round(r.age_h / 24)}j`}</span>
                    </div>
                  );
                })}
                {dash.a_traiter.length === 0 && <div className="py-8 text-center text-slate-400 text-sm italic">Rien 🎉</div>}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="mkt-calendar">
              <h3 className="font-display text-base font-bold text-slate-900 mb-2">Calendrier commercial</h3>
              <div className="divide-y divide-slate-100">
                {dash.calendrier.map(c => {
                  const d = new Date(c.date_debut);
                  return (
                    <div key={c.id} className="py-2 flex items-center gap-3" data-testid={`mkt-cal-${c.id}`}>
                      <div className="w-11 h-11 rounded-lg bg-blue-50 grid place-items-center text-blue-700 shrink-0 text-center">
                        <div className="text-[15px] font-bold leading-none">{String(d.getDate()).padStart(2,"0")}</div>
                        <div className="text-[9px] font-bold uppercase leading-none mt-0.5">{d.toLocaleDateString("fr-FR",{month:"short"}).replace(".","")}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate">{c.nom}</div>
                        <div className="text-[10px] text-slate-500">{c.corners_count} corners</div>
                      </div>
                      <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap">Dans {c.dans_jours}j</span>
                    </div>
                  );
                })}
                {dash.calendrier.length === 0 && <div className="py-8 text-center text-slate-400 text-sm italic">RAS</div>}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="mkt-conformite">
              <h3 className="font-display text-base font-bold text-slate-900">Déploiement des campagnes</h3>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="font-display text-4xl font-bold text-slate-900">{dash.conformite.taux}%</div>
                <div className="text-[11px] font-semibold text-slate-500">conformité réseau</div>
              </div>
              <div className="mt-3 space-y-1.5 text-xs">
                <Row lbl="Conformes" v={dash.conformite.conformes} color="bg-emerald-500"/>
                <Row lbl="Incomplets" v={dash.conformite.incomplets} color="bg-amber-500"/>
                <Row lbl="Non démarrés" v={dash.conformite.non_demarres} color="bg-rose-500"/>
              </div>
              {dash.non_prets.length > 0 && (
                <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-[11px] text-rose-800">
                  <b>{dash.non_prets.length} corners</b> à moins de 50% sur des campagnes proches.
                </div>
              )}
            </div>
          </div>

          {/* Row 2 : Répartition + Activité équipe */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="mkt-repartition">
              <h3 className="font-display text-base font-bold text-slate-900 mb-2">Demandes par catégorie</h3>
              {dash.repartition.length ? (
                <div style={{ height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={dash.repartition.map(x => ({...x, l: CAT_LABEL[x.k] || x.k}))} dataKey="count" nameKey="l" innerRadius={44} outerRadius={70} paddingAngle={2}>
                        {dash.repartition.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]}/>)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 6, fontSize: 11 }}/>
                      <Legend wrapperStyle={{ fontSize: 10 }}/>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : <div className="py-12 text-center text-slate-400 text-sm italic">Aucune demande</div>}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="mkt-activite">
              <h3 className="font-display text-base font-bold text-slate-900 mb-2">Activité équipe (7j)</h3>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div className="bg-slate-50 rounded-xl py-3"><div className="font-display text-2xl font-bold text-slate-900">{dash.activite.recues}</div><div className="text-[10px] font-bold uppercase text-slate-500 mt-1">Reçues</div></div>
                <div className="bg-emerald-50 rounded-xl py-3"><div className="font-display text-2xl font-bold text-emerald-700">{dash.activite.terminees}</div><div className="text-[10px] font-bold uppercase text-emerald-700 mt-1">Terminées</div></div>
                <div className="bg-blue-50 rounded-xl py-3"><div className="font-display text-2xl font-bold text-blue-700">{dash.activite.respect_delais}%</div><div className="text-[10px] font-bold uppercase text-blue-700 mt-1">Respect délais</div></div>
              </div>
              {dash.activite.recues_prev >= 0 && (
                <div className="mt-3 text-[11px] text-slate-500">Semaine précédente : <b>{dash.activite.recues_prev}</b> reçues</div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="mkt-campagnes-encours">
              <h3 className="font-display text-base font-bold text-slate-900 mb-2">Campagnes en cours</h3>
              <div className="divide-y divide-slate-100">
                {dash.campagnes_en_cours.map(c => (
                  <div key={c.id} className="py-2" data-testid={`mkt-cec-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold text-slate-800 truncate">{c.nom}</div>
                      <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${CAMP_STAT_CLS[c.statut] || "bg-slate-100"}`}>{c.statut}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${c.deployment_pct >= 80 ? "bg-emerald-500" : c.deployment_pct >= 40 ? "bg-blue-500" : "bg-amber-500"}`} style={{ width: `${c.deployment_pct}%` }}/>
                      </div>
                      <span className="text-[10px] font-bold text-slate-500 w-8 text-right">{c.deployment_pct}%</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{c.corners_count} corners</div>
                  </div>
                ))}
                {dash.campagnes_en_cours.length === 0 && <div className="py-8 text-center text-slate-400 text-sm italic">Aucune campagne</div>}
              </div>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="sticky bottom-3 z-10 bg-white border border-slate-200 rounded-2xl shadow-lg p-2.5 flex flex-wrap gap-2 justify-center" data-testid="mkt-quick">
            <Quick icon={Plus} label="Nouvelle demande" testid="mkt-q-req"/>
            <Quick icon={Rocket} label="Créer une campagne" testid="mkt-q-camp" onClick={() => { window.location.href = "/campaigns?new=1"; }}/>
            <Quick icon={Sparkles} label="Créer une animation" testid="mkt-q-anim"/>
            <Quick icon={MessageSquare} label="Diffuser une communication" testid="mkt-q-comm"/>
            <Quick icon={Package} label="Ajouter un support" testid="mkt-q-media"/>
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  OFFRE / CAMPAGNES
// ═════════════════════════════════════════════════════════════════════════
function OffreTab() {
  const [camps, setCamps] = useState([]); const [medias, setMedias] = useState([]); const [loading, setLoading] = useState(true); const [fiche, setFiche] = useState(null);
  const load = async () => { setLoading(true); try { const [c, m] = await Promise.all([api.get("/marketing/campaigns"), api.get("/marketing/media")]); setCamps(c.data || []); setMedias(m.data || []); } catch (e) { toast.error(e.message); } setLoading(false); };
  useEffect(() => { load(); }, []);
  const now = new Date().toISOString().slice(0,10);
  const groups = {
    actives: camps.filter(c => c.date_debut <= now && c.date_fin >= now && c.statut !== "terminee"),
    avenir: camps.filter(c => c.date_debut > now),
    terminees: camps.filter(c => c.statut === "terminee" || c.date_fin < now),
  };
  return (
    <div className="space-y-3" data-testid="mkt-offre">
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Offre & Campagnes</h1>
      {loading ? <div className="py-24 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CampsGroup title={`En cours (${groups.actives.length})`} rows={groups.actives} onOpen={setFiche} accent="bg-emerald-500"/>
            <CampsGroup title={`À venir (${groups.avenir.length})`} rows={groups.avenir} onOpen={setFiche} accent="bg-blue-500"/>
            <CampsGroup title={`Terminées (${groups.terminees.length})`} rows={groups.terminees} onOpen={setFiche} accent="bg-slate-400"/>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="mkt-mediatheque">
            <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-display text-base font-bold text-slate-900">Médiathèque · Derniers supports validés</h3></div>
            <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              {medias.slice(0, 12).map(m => (
                <div key={m.id} className="border border-slate-200 rounded-xl p-2.5 hover:border-blue-300 cursor-pointer" data-testid={`mkt-media-${m.id}`}>
                  <div className="aspect-video bg-slate-100 rounded-lg grid place-items-center text-slate-400 mb-2"><Package className="w-6 h-6"/></div>
                  <div className="text-xs font-semibold text-slate-800 truncate">{m.nom}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[9px] uppercase font-bold text-slate-500">{m.categorie}</span>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${m.statut === "valide" ? "bg-emerald-100 text-emerald-700" : m.statut === "a_remplacer" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{m.statut === "valide" ? "Validé" : m.statut === "a_remplacer" ? "À remplacer" : m.statut === "expire" ? "Expiré" : "Archivé"}</span>
                  </div>
                </div>
              ))}
              {medias.length === 0 && <div className="col-span-full py-8 text-center text-slate-400 text-sm italic">Aucun support</div>}
            </div>
          </div>
        </>
      )}
      {fiche && <CampaignFicheDrawer id={fiche} onClose={() => { setFiche(null); load(); }}/>}
    </div>
  );
}

function CampsGroup({ title, rows, onOpen, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className={`h-1 ${accent}`}/>
      <div className="p-3 border-b border-slate-100"><h3 className="font-display text-sm font-bold text-slate-900">{title}</h3></div>
      <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
        {rows.map(c => (
          <button key={c.id} onClick={() => onOpen(c.id)} className="w-full px-3 py-2.5 text-left hover:bg-blue-50/40" data-testid={`mkt-camp-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-slate-800 truncate flex-1">{c.nom}</div>
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${CAMP_STAT_CLS[c.statut] || "bg-slate-100"}`}>{c.statut}</span>
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">{c.date_debut} → {c.date_fin} · {(c.corner_keys || []).length} corners</div>
            <div className="mt-1.5 flex items-center gap-2">
              <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${c.deployment_pct >= 80 ? "bg-emerald-500" : c.deployment_pct >= 40 ? "bg-blue-500" : "bg-amber-500"}`} style={{ width: `${c.deployment_pct || 0}%` }}/>
              </div>
              <span className="text-[10px] font-bold text-slate-500 w-8 text-right">{c.deployment_pct || 0}%</span>
            </div>
          </button>
        ))}
        {rows.length === 0 && <div className="py-8 text-center text-slate-400 text-xs italic">Aucune campagne</div>}
      </div>
    </div>
  );
}

function CampaignFicheDrawer({ id, onClose }) {
  const [c, setC] = useState(null); const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { setLoading(true); try { const r = await api.get(`/marketing/campaigns/${id}`); setC(r.data); } catch (e) { toast.error(e.message); onClose(); } setLoading(false); })(); }, [id]);
  const toggle = async (p, k, done) => { try { await api.patch(`/marketing/campaigns/${id}/progress/${p.id}/action`, { k, done }); const r = await api.get(`/marketing/campaigns/${id}`); setC(r.data); } catch (e) { toast.error(e.message); } };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="fixed inset-y-0 right-0 w-full sm:w-[820px] bg-white shadow-2xl border-l flex flex-col" data-testid="mkt-camp-drawer">
        <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">{c?.nom || "Campagne"}</h2>
            <div className="text-xs text-slate-500 mt-0.5">{c?.date_debut} → {c?.date_fin} · {(c?.corner_keys || []).length} corners</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading || !c ? <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div> : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 rounded-xl p-3"><div className="text-[10px] uppercase font-bold text-emerald-700">Conformes</div><div className="font-display text-2xl font-bold text-emerald-900 mt-1">{c.conformite.conformes}</div></div>
                <div className="bg-amber-50 rounded-xl p-3"><div className="text-[10px] uppercase font-bold text-amber-700">Incomplets</div><div className="font-display text-2xl font-bold text-amber-900 mt-1">{c.conformite.incomplets}</div></div>
                <div className="bg-rose-50 rounded-xl p-3"><div className="text-[10px] uppercase font-bold text-rose-700">Non démarrés</div><div className="font-display text-2xl font-bold text-rose-900 mt-1">{c.conformite.non_demarres}</div></div>
              </div>

              <div>
                <div className="text-[11px] font-bold uppercase text-slate-500 mb-2 flex items-center gap-1.5"><Target className="w-3 h-3"/> Matrice de déploiement</div>
                <div className="overflow-x-auto max-h-[400px] border border-slate-200 rounded-xl">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-2 font-bold uppercase text-[9px] text-slate-500">Corner</th>
                        {(c.actions || []).map(a => <th key={a.k} className="px-1 py-2 text-center text-[9px] font-bold text-slate-500" title={a.l}>{a.l.slice(0,6)}</th>)}
                        <th className="px-2 py-2 text-right text-[9px] font-bold text-slate-500">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(c.progress || []).slice(0, 100).map(p => {
                        const n = (p.actions || []).length || 1;
                        const done = (p.actions || []).filter(a => a.done).length;
                        const pct = Math.round(100 * done / n);
                        return (
                          <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`mkt-mat-${p.id}`}>
                            <td className="px-2 py-1.5 font-semibold text-slate-800 whitespace-nowrap max-w-[180px] truncate">{p.corner || p.corner_key}</td>
                            {(p.actions || []).map(a => (
                              <td key={a.k} className="px-1 py-1.5 text-center">
                                <button onClick={() => toggle(p, a.k, !a.done)} data-testid={`mkt-mat-cell-${p.id}-${a.k}`}
                                        className={`w-5 h-5 rounded ${a.done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}>
                                  {a.done ? "✓" : "·"}
                                </button>
                              </td>
                            ))}
                            <td className={`px-2 py-1.5 text-right font-bold ${pct === 100 ? "text-emerald-600" : pct >= 50 ? "text-amber-600" : "text-rose-600"}`}>{pct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  ACTIVITÉ · Demandes & Production
// ═════════════════════════════════════════════════════════════════════════
function ActiviteTab({ filters }) {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [q, setQ] = useState(""); const [statut, setStatut] = useState("");
  const load = async () => { setLoading(true); try { const r = await api.get("/marketing/requests", { params: { ...filters, statut } }); setRows(r.data || []); } catch (e) { toast.error(e.message); } setLoading(false); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.dr, filters.secteur, filters.corner_key, statut]);
  const filtered = rows.filter(r => !q || `${r.sujet} ${r.corner || ""} ${r.numero}`.toLowerCase().includes(q.toLowerCase()));
  const setStat = async (r, s) => { try { await api.patch(`/marketing/requests/${r.id}`, { statut: s }); toast.success("Statut mis à jour"); load(); } catch (e) { toast.error(e.message); } };
  const tabs = ["", "a_qualifier", "en_production", "a_valider", "attente_terrain", "cloturee"];
  return (
    <div className="space-y-3" data-testid="mkt-activite">
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Demandes & Production</h1>
      <div className="flex flex-wrap gap-1">
        {tabs.map(k => (
          <button key={k || "all"} onClick={() => setStatut(k)} data-testid={`mkt-req-tab-${k || "all"}`}
                  className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${statut === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {k ? (REQ_STAT_LABEL[k] || k) : "Toutes"}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher…" data-testid="mkt-req-search"
                   className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none focus:ring-2 focus:ring-blue-100"/>
          </div>
        </div>
        {loading ? <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div> : (
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Priorité</th>
                  <th className="text-left px-3 py-2">N°</th>
                  <th className="text-left px-3 py-2">Sujet</th>
                  <th className="text-left px-3 py-2">Catégorie</th>
                  <th className="text-left px-3 py-2">Corner</th>
                  <th className="text-left px-3 py-2">Reçue</th>
                  <th className="text-left px-3 py-2">Responsable</th>
                  <th className="text-left px-3 py-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const p = PRIO[r.priorite] || PRIO.standard;
                  return (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-blue-50/40" data-testid={`mkt-req-row-${r.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${p.cls}`}>{p.l}</span></td>
                      <td className="px-3 py-2 text-slate-500 text-[11px] whitespace-nowrap">{r.numero}</td>
                      <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap max-w-[260px] truncate">{r.sujet}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{CAT_LABEL[r.categorie] || r.categorie}</td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[160px] truncate">{r.corner || "—"}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
                      <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{r.assignee_nom || "—"}</td>
                      <td className="px-3 py-2">
                        <SearchSelect value={r.statut} onChange={v => setStat(r, v)} testid={`mkt-req-stat-${r.id}`} size="xs" menuWidth={200}
                                      options={Object.entries(REQ_STAT_LABEL).map(([k, l]) => ({ value: k, label: l }))}/>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-slate-400 text-sm italic">Aucune demande</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shared micro-components ────────────────────────────────────────────
function Kpi({ icon: Icon, c, title, v, unit, s, testid }) {
  const bg = { blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600", rose: "bg-rose-50 text-rose-600", purple: "bg-purple-50 text-purple-600", amber: "bg-amber-50 text-amber-600", orange: "bg-orange-50 text-orange-600" }[c] || "bg-slate-50 text-slate-600";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg ${bg} grid place-items-center`}><Icon className="w-4 h-4"/></div>
      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-2">{title}</div>
      <div className="flex items-baseline gap-1"><div className="font-display text-2xl font-bold text-slate-900">{v}</div>{unit && <div className="text-xs font-semibold text-slate-500">{unit}</div>}</div>
      {s && <div className="text-[10px] text-slate-500 mt-0.5">{s}</div>}
    </div>
  );
}
function Quick({ icon: Icon, label, testid, onClick }) { return <button data-testid={testid} onClick={onClick} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 border border-slate-100"><Icon className="w-3.5 h-3.5"/>{label}</button>; }
function Row({ lbl, v, color }) { return <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${color}`}/><span className="text-slate-600 flex-1">{lbl}</span><b className="text-slate-800">{v}</b></div>; }
function FSelect({ value, onChange, options, placeholder, testid, enrich, single = false }) {
  const opts = (options || []).map(o => typeof o === "string" ? { value: o, label: o } : { value: o.v, label: enrich ? o.l : (o.l || o.v) });
  return <FilterMultiSelect label={placeholder} placeholder={placeholder} testid={testid} options={opts} single={single} size="lg" menuWidth={240}
                            value={(value || "").split(",").filter(Boolean)} onChange={arr => onChange(arr.join(","))}/>;
}
